import { toast } from 'sonner';
import { useState } from 'react';
import { CheckCircle2, FileText, Sparkles } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useGenerateDocument } from '../api/documents-api';
import { useTemplates } from '@/features/templates/api/templates-api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface GenerateDocumentDialogProps {
  encounterId: string;
  patientId: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function GenerateDocumentDialog({ open, encounterId, patientId, onOpenChange }: GenerateDocumentDialogProps) {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [generated, setGenerated] = useState(false);
  const [notes, setNotes] = useState('');

  const { data: templatesData } = useTemplates({ active_only: true });
  const templates = templatesData?.data ?? [];
  const selectedTmpl = templates.find((t) => t.id === selectedTemplate);

  const generateMutation = useGenerateDocument();

  const handleGenerate = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a document template');
      return;
    }
    try {
      await generateMutation.mutateAsync({
        template_id: selectedTemplate,
        patient_id: patientId,
        encounter_ids: [encounterId],
        additional_context: notes ? { notes } : null,
      });
      setGenerated(true);
      toast.success(`${selectedTmpl?.name || 'Document'} generated successfully`);
    } catch {
      toast.error('Failed to generate document. Please try again.');
    }
  };

  const handleClose = () => {
    setSelectedTemplate('');
    setNotes('');
    setGenerated(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            Generate Document
          </DialogTitle>
          <DialogDescription>
            Generate an AI-powered document from this encounter's transcript.
          </DialogDescription>
        </DialogHeader>

        {generated ? (
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle2 className="h-12 w-12 text-green-500 mb-3" />
            <p className="font-medium text-lg">Document Generated</p>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedTmpl?.name} has been created successfully.
            </p>
            <Badge variant="secondary" className="mt-3 gap-1">
              <FileText className="h-3 w-3" />
              Status: Draft
            </Badge>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Template Selection */}
            <div className="space-y-2">
              <Label>Document Template</Label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((tmpl) => (
                    <SelectItem key={tmpl.id} value={tmpl.id}>
                      <div className="flex flex-col">
                        <span>{tmpl.name}</span>
                        {tmpl.description && (
                          <span className="text-xs text-muted-foreground">{tmpl.description}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Notes (shown for all templates) */}
            {selectedTemplate && (
              <div className="space-y-2">
                <Label>Additional Notes (Optional)</Label>
                <Textarea
                  rows={3}
                  value={notes}
                  placeholder="Any additional context for document generation..."
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            )}

            {/* Info */}
            {selectedTemplate && (
              <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
                The document will be generated using the encounter transcript as the source of truth.
                You can review and edit the document after generation.
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {generated ? (
            <Button className="w-full" onClick={handleClose}>Done</Button>
          ) : (
            <>
              <Button variant="outline" disabled={generateMutation.isPending} onClick={handleClose}>
                Cancel
              </Button>
              <Button disabled={!selectedTemplate || generateMutation.isPending} onClick={handleGenerate}>
                {generateMutation.isPending ? (
                  <>
                    <Sparkles className="mr-2 h-4 w-4 animate-pulse" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="mr-2 h-4 w-4" />
                    Generate
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
